<script setup lang="ts">
import {ref, computed} from "vue";
import {kdm, editMode, addAxis, deleteAxis, checkAxisUsage, loadKdm, openAxisRecycle} from "@/store/kdm";
import KDMViewAxisToggle from "@/kdm/KDMViewAxisToggle.vue";
import KdmEditableAxisLabel from "@/kdm/KdmEditableAxisLabel.vue";

const addingColumn = ref(false)
const newColumnTitle = ref('')

const canManageAxis = computed(() => {
  const cu = kdm.value?.current_user;
  return cu?.is_admin || cu?.is_owner || cu?.is_coordinator || cu?.is_editor;
});

// All KDM types can add a column: types 1/2 directly, type 3 cascades to the
// linked KRR (the KDM inherits it).
const canAddColumn = computed(() => !!kdm.value);

async function handleAddColumn() {
  if (!newColumnTitle.value.trim()) return
  addingColumn.value = true
  const success = await addAxis('column', newColumnTitle.value.trim())
  if (success) newColumnTitle.value = ''
  addingColumn.value = false
}

async function handleDeleteColumn(crossLinkId: string, title: string) {
  if (!confirm(`Supprimer la colonne "${title}" ?`)) return
  const success = await deleteAxis('column', crossLinkId, false)
  if (success && kdm.value?.nid) {
    await loadKdm(String(kdm.value.nid))
  }
}
</script>

<template>
  <tr v-if="kdm">
    <th class="corner-th"><KDMViewAxisToggle/></th>
    <th v-for="column in kdm.columns" :key="column.id" :title="column.is_in_kik === false ? 'Indicator/Argument no longer contextualized in the KIK' : ''">
      <div class="th-content">
        <KdmEditableAxisLabel
          :axis="column"
          :can-edit="editMode && canManageAxis"
          :link-class="[column.moderation_class, column.is_in_kik === false ? 'fw-bold' : '']"
          icon-stroke="white"
        >
          <template #actions>
            <button
              v-if="editMode && canManageAxis"
              type="button"
              class="btn-delete-axis"
              @click.stop="handleDeleteColumn(column.crossLinkId, column.title)"
              title="Delete"
            >
              <i class="bi bi-trash"></i>
            </button>
          </template>
        </KdmEditableAxisLabel>
      </div>
    </th>
    <th v-if="editMode && canManageAxis" class="add-axis-th">
      <div class="add-axis-cell">
        <template v-if="canAddColumn">
          <input
            v-model="newColumnTitle"
            type="text"
            class="form-control form-control-sm"
            placeholder="New TOC..."
            @keydown.enter="handleAddColumn"
            :disabled="addingColumn"
          />
          <button
            class="btn btn-sm btn-outline-light"
            @click="handleAddColumn"
            :disabled="addingColumn || !newColumnTitle.trim()"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </template>
        <button
          type="button"
          class="btn btn-sm btn-success"
          style="background-color: #198754; border-color: #198754; color: white; font-weight: bold;"
          @click="openAxisRecycle('column')"
          title="Recycle existing value"
        >
          <i class="bi bi-plus-lg"></i>
        </button>
      </div>
    </th>
  </tr>
</template>

<style scoped>
tr {
  th {
    position: sticky;
    top: 0;
    background-color: #6c757d;
    color: #fff;
    z-index: 2;
    text-align: left;
    padding: 10px;
    border-bottom: 2px solid #ccc;
    border: 1px solid #ccc;
    font-size: 14px;
    width: var(--kdm-column-width, 150px);
    min-width: var(--kdm-column-width, 150px);
    max-width: var(--kdm-column-width, 150px);
    vertical-align: top;

    a {
      color: #fff;
      font-weight: 600;
      text-decoration: none;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      display: block;
    }

    &:hover {
      a {
        color: #fff;
        font-weight: 600;
        text-decoration: underline dotted;
      }
    }
  }

  .th-content {
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }

  .corner-th {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
    text-align: left;
    vertical-align: middle;
    padding: 8px;
  }

  .btn-delete-axis {
    background: transparent;
    border: none;
    color: #ffcccc;
    cursor: pointer;
    padding: 2px 4px;
    font-size: 12px;
    opacity: 0.7;
    &:hover {
      color: #ff6666;
      opacity: 1;
    }
  }

  /* Fixed-width "add column" cell so it never balloons to absorb the table's
     remaining width (which crushed the existing columns). Input on the left,
     the +/recycle icons compact on its right. */
  .add-axis-th {
    width: 240px;
    min-width: 240px;
    max-width: 240px;
    vertical-align: top;
    padding: 8px;
  }

  .add-axis-cell {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  .add-axis-cell input {
    flex: 1 1 auto;
    min-width: 0;
  }

  .add-axis-cell button {
    flex: 0 0 auto;
  }
}
</style>
